import { ChevaletIcon } from 'app-v2/assets/icons'
import { IconButton } from 'app-v2/design-systems/components/inputs/button'
import { COLOR } from 'app-v2/design-tokens'
import { BagOnClick, Close } from 'app/assets/icons'
import { style } from 'app/assets/styles'
import { QRCodeScannerModal } from 'app/common/qr-code-scanner/QRCodeScannerModal'
import { H3 } from 'app/common/typography'
import React, { useCallback, useEffect, useState } from 'react'
import { isMobile, isTablet } from 'react-device-detect'
import { useIntl } from 'react-intl'
import Modal from 'react-modal'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import type { RootState } from 'shared/store/reducers'
import { featureCodes } from 'shared/utils'
import {
    StyledBody,
    StyledButtonContainer,
    StyledCloseButtonContainer
} from './styles'
import { parseKingTableUrl } from './utils'
import type { RestaurantShortDTO } from 'shared/types'

type ChooseOrderTypeModalProps = {
    visible: boolean,
    close: () => void,
    restaurant?: RestaurantShortDTO,
    handleCcClick?: any
}

export const ChooseOrderTypeModal = ({
    close,
    visible,
    restaurant,
    handleCcClick
}: ChooseOrderTypeModalProps) => {
    const intl = useIntl()
    const history = useHistory()

    const tableOrderEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.TABLE_ORDER)
    )

    const newOrderFlowEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.ENABLE_NEW_ORDER_FLOW)
    )

    const goToCC = useCallback(() => {
        if (!!restaurant) {
            !!handleCcClick && handleCcClick()
        } else {
            close()
            if (newOrderFlowEnabled) {
                document.location.href = intl.formatMessage({
                    id: 'routes.clickandcollect.restaurant.choice'
                })
            } else {
                history.push(
                    intl.formatMessage({
                        id: 'routes.clickandcollect.restaurant.choice'
                    })
                )
            }
        }
    }, [close, history, intl])

    const goToKingTable = useCallback(
        (params: {
            restaurant: string,
            table: string,
            terrace: string,
            token: string
        }) => {
            const urlParam = new URLSearchParams()
            urlParam.set('restaurant', params.restaurant)
            urlParam.set('table', params.table)
            urlParam.set('terrace', params.terrace)
            urlParam.set('token', params.token)
            close()
            if (newOrderFlowEnabled) {
                document.location.href =
                    intl.formatMessage({
                        id: 'routes.kingtable.full'
                    }) + `?${urlParam.toString()}`
            } else {
                history.push(
                    intl.formatMessage({
                        id: 'routes.kingtable.full'
                    }) + `?${urlParam.toString()}`
                )
            }
        },
        [close, history, intl]
    )

    const goToNoDesktop = useCallback(() => {
        close()
        if (newOrderFlowEnabled) {
            window.location.href = '/commande/kingtable'
        } else {
            history.push(
                intl.formatMessage({
                    id: 'routes.kingtable.no-desktop'
                })
            )
        }
    }, [close, history, intl])

    const [scannerOpen, setScannerOpen] = useState(false)

    useEffect(() => {
        if (visible && !tableOrderEnabled) {
            goToCC() // we don't show the modal and automatically redirect to CC if it's the only option
        }
    }, [tableOrderEnabled, visible])

    if (visible && !tableOrderEnabled) {
        return null
    }

    return (
        <Modal
            isOpen={visible}
            ariaHide={false}
            shouldCloseOnOverlayClick
            onRequestClose={close}
            style={style.classicModalStyle}>
            <StyledCloseButtonContainer onClick={close}>
                <Close color={COLOR.PRIMARY} size={24} />
            </StyledCloseButtonContainer>
            <StyledBody>
                <H3>
                    {intl.formatMessage({
                        id: 'ecommerce.order.mode.choice'
                    })}
                </H3>
                <StyledButtonContainer>
                    <IconButton
                        fullLength
                        variant={'outlined'}
                        buttonColor={COLOR.PRIMARY}
                        labelColor={COLOR.PRIMARY}
                        onClick={goToCC}
                        otherStyle={{ fontSize: '16px' }}
                        icon={<BagOnClick color={COLOR.PRIMARY} size={24} />}>
                        {intl.formatMessage({
                            id: 'clickandcollect'
                        })}
                    </IconButton>
                    {tableOrderEnabled && (isMobile || isTablet) && (
                        <IconButton
                            fullLength
                            variant={'outlined'}
                            buttonColor={COLOR.PRIMARY}
                            labelColor={COLOR.PRIMARY}
                            otherStyle={{ fontSize: '16px' }}
                            icon={
                                <ChevaletIcon color={COLOR.PRIMARY} size={24} />
                            }
                            onClick={() => {
                                if (isMobile || isTablet) {
                                    if (newOrderFlowEnabled) {
                                        window.location.href =
                                            '/commande/kingtable-qr-code'
                                    } else {
                                        setScannerOpen(true)
                                    }
                                } else {
                                    goToNoDesktop()
                                }
                            }}>
                            {intl.formatMessage({
                                id: 'kingtable'
                            })}
                        </IconButton>
                    )}
                </StyledButtonContainer>
            </StyledBody>
            <QRCodeScannerModal
                helpText={intl.formatMessage({
                    id: 'ecommerce.kingtable.scan.description'
                })}
                open={scannerOpen}
                idleColor={COLOR.PRIMARY}
                onClose={() => {
                    setScannerOpen(false)
                    close && close()
                }}
                onScan={result => {
                    const parsed = parseKingTableUrl(result)

                    if (!parsed) {
                        throw new Error(
                            intl.formatMessage({
                                id: 'qr.code.table.error'
                            })
                        )
                    }

                    goToKingTable(parsed)
                }}
            />
        </Modal>
    )
}
